import React from 'react';
import moment from 'moment';
import * as H from "../../hooks";
import { TB, T } from "../../Constants";
import { Link } from 'react-router-dom';
import Avatar, { AvatarProps } from './Avatar';

type NotificationProps = {
    /** The time at which the notification was created (ISO) */
    time: string;
    /** The emoji to display in the notification */
    emoji?: string;
    /** Whether the notification should be flushed */
    flush?: boolean;
    /** Whether the notification is read */
    read?: boolean;
    /** The link to redirect to */
    link?: string;
    /** Additional classes to apply to the notification */
    className?: string;
    /** The avatar to display in the notification */
    avatar?: AvatarProps;
    /** Choose how to display the time */
    show_time?: "relative" | "exact" | "calendar";
    /** The content to display in the notification */
    children?: T.AllowArray<React.ReactNode>;
    /** The text to display*/
    texts?: T.Notification["text"];
    /** Callback to set the notification as read */
    on_read: () => void;
};

const Notification: React.FC<NotificationProps> = ({ on_read, ...props }) => {
    const lg = H.useLanguage();

    const time_formatted = React.useMemo(() => {
        const date = TB.getDate(props.time);
        const date_moment = moment(date);
        if (props.show_time === "calendar") return date_moment.calendar();
        else if (props.show_time === "exact") return date_moment.format("llll");
        else return date_moment.fromNow();
    }, [props.time, lg.language, props.show_time]);

    const className = React.useMemo(() => {
        const classes = ["notification"];
        if (!props.read) classes.push("alert-warning");
        if (props.flush) classes.push("notification-flush");
        return classes.join(" ");
    }, [props.read, props.flush]);

    const avatar = React.useMemo(() => {
        if (!props.avatar) return null;
        return <div className="notification-avatar">
            <Avatar {...props.avatar} className="me-3" />
        </div>
    }, [props.avatar]);

    const content = React.useMemo(() => {
        if (props.children) return props.children;
        else {
            const texts = TB.arrayWrapper(props.texts);
            const string_contents = texts.map((text, index) => <div key={index}>{lg.getStaticText(text.ref, ...(text.templates || []))}</div>);
            return string_contents;
        }
    }, [props.children, props.texts, lg]);

    return <Link className={className} onClick={on_read} to={props.link || "#!"}>
        {avatar}

        <div className="notification-body">

            <div className='mb-1' children={content} />

            <span className="notification-time fs-85">
                {props.emoji && <span className="me-2" role="img" aria-label="Emoji" children={props.emoji} />}
                <span className='text-muted'>{time_formatted}</span>
            </span>
        </div>
    </Link>;
}

export default Notification;
